<script setup lang="ts">
import { THEME_COLOR } from "~/constants";
import { getTagUrl } from "~/utils/tags";
import { formatTime } from "~/utils/time";

export interface TagProps {
  id: string;
  title?: string;
  type: TTagType;
  hasMenu?: boolean;
  hasMenuDisabled?: boolean;
  startTime?: number;
  endTime?: number;
  playingTime?: number;
  playerNumber?: string;
  teamColor?: string;
  teamName?: string;
  isPlaying?: boolean;
}
const props = withDefaults(defineProps<TagProps>(), {
  isPlaying: false,
  hasMenu: false,
  type: "general_drill",
});

const { t } = useI18n();
const color = useColorMode();
const startTime = formatTime(props.startTime);
const isPlaying = computed(() => {
  if (props.endTime && (props.startTime || props.startTime === 0) && props.playingTime)
    return props.isPlaying && (props.playingTime >= props.startTime && props.playingTime <= props.endTime);

  return false;
});
const progressBarPercentage = computed(() => {
  if (props.endTime && props.startTime && props.playingTime)
    return (props.playingTime - props.startTime) * 100 / (props.endTime - props.startTime);

  return 0;
});

const showPlayIcon = ref(false);
const tagSrc = getTagUrl(props.type);
const emit = defineEmits(["edit", "delete", "stop", "play"]);
const onClickEdit = () => emit("edit", props.id);
const onClickStop = () => emit("stop");
const onClickPlay = () => emit("play", props.startTime);
const onClickDelete = () => emit("delete", props.id);
const onTagIconHover = () => showPlayIcon.value = true;
const onTagIconLeave = () => showPlayIcon.value = false;
</script>

<template>
  <div class="BaseTag" :class="{ BaseTag__playing: isPlaying }">
    <div class="BaseTag__left">
      <div class="sm:pr-3 flex gap-2 sm:gap-4">
        <slot name="start" />

        <div class="flex gap-4 items-center" @mouseenter="onTagIconHover" @mouseleave="onTagIconLeave">
          <BaseButton v-if="isPlaying" name="play-button" type="icon" @click="onClickStop()">
            <IconPlayingTag :is-dark-theme="color.value === THEME_COLOR.DARK" :percentage="progressBarPercentage" />
          </BaseButton>
          <IconTagType v-else-if="props.type && !isPlaying" :src="tagSrc" :show-play="showPlayIcon" @click="onClickPlay()" />
        </div>
      </div>
      <div class="text-main text-xs sm:text-base lg:max-xl:text-sm truncate mx-2">
        {{ props.title }}
      </div>
    </div>

    <div class="BaseTag__right">
      <div v-if="!isPlaying" class="flex items-center justify-end w-full cursor-default">
        <div
          v-if="props.playerNumber && props.teamColor"
          v-tooltip="props.teamName"
          class="flex items-center
          w-[3rem] sm:w-[4rem] lg:max-xl:w-[3.5rem] justify-start gap-[2px] sm:gap-2 font-medium text-xs sm:text-base lg:max-xl:text-xs"
        >
          <IconTshirt :fill="props.teamColor" />
          <span>
            # {{ props.playerNumber }}
          </span>
        </div>
        <div v-if="props.playerNumber && props.teamColor" class="border h-4 mx-1 sm:mx-2 lg:max-xl:mx-1 border-[var(--base-card-bgSecondaryColor)]" />
        <div class="BaseTag__time text-main">
          {{ startTime }}
        </div>
      </div>

      <IconEqualizer v-if="isPlaying" class="mx-4" />

      <UMenu>
        <template #trigger="{ show }">
          <BaseButton name="menu" title="Menu" type="icon" class="dropdown__icon" :disabled="props.hasMenuDisabled" @click.stop="show">
            <IconAction />
          </BaseButton>
        </template>
        <template #default>
          <UList>
            <UListItem size="md">
              <BaseButton
                class="w-full text-secondary text-link row-center"
                type="icon"
                name="edit tag button"
                @click.prevent="onClickEdit"
              >
                <div class="flex items-center truncate gap-2 text-main">
                  <IconEdit />
                  {{ t('button.edit') }}
                </div>
              </BaseButton>
            </UListItem>
            <UListItem size="md" class="!text-[var(--base-error-color)]">
              <BaseButton
                class="w-full text-link row-center"
                type="icon"
                name="delete tag button"
                @click.prevent="onClickDelete"
              >
                <div class="flex items-center truncate gap-2">
                  <IconDelete fill="var(--base-error-color)" />
                  {{ t('button.delete') }}
                </div>
              </BaseButton>
            </UListItem>
          </UList>
        </template>
      </UMenu>
    </div>
  </div>
</template>

<style lang="scss">
.BaseTag {
  @apply w-full h-[56px] p-4 flex items-center justify-end transition duration-100;

  &:hover {
    background-color: var(--base-playing-tag-bgColor);
  }

  &.BaseTag__playing {
    background-color: var(--base-playing-tag-bgColor);
  }
}

.BaseTag__left {
  @apply w-1/2 flex items-center justify-start;
}

.BaseTag__right {
  @apply w-1/2 flex items-center justify-end;

  .BaseTag__time {
    @apply truncate w-[3rem] sm:w-[4rem] lg:max-xl:w-[3rem] text-xs sm:text-base lg:max-xl:text-xs flex justify-center font-medium;
  }

  .dropdown__icon {
  .item__dot {
    height: 3px;
    width: 3px;
    border-radius: 50%;
    display: inline-block;
    background-color: var(--base-secondary-color);

    &.item__dot_middle {
      @apply my-0.5;
    }
  }
  &:focus-within .item__dot {
    background-color: var(--base-error-color);
  }
}
}
</style>
